import { QueryClient } from "@tanstack/react-query";
import { createRouter, type AnyRouter } from "@tanstack/react-router";
import * as Sentry from "@sentry/tanstackstart-react";
import {
  attachDepthPushVtBridge,
  depthPushViewTransitionTypes,
} from "@/lib/depthPushTransition";
import { startDiscoverySplashPrefetch } from "@/lib/discoverySplashPrefetch";
import { DEFAULT_LOCATION, readStoredCity } from "@/lib/location";
import { routeTree } from "./routeTree.gen";

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
      },
    },
  });
}

function createRouterInstance(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    // Depth Push: Discovery ↔ Event/Venue via View Transitions (see depth-push.css).
    // Other routes get type "instant" (no visible animation). Reduced motion → instant.
    defaultViewTransition: {
      types: depthPushViewTransitionTypes,
    },
  });
}

let browserQueryClient: QueryClient | undefined;
let browserRouter: AnyRouter | undefined;
let browserTracingAttached = false;

function getQueryClient() {
  if (typeof window === "undefined") {
    return createQueryClient();
  }

  if (!browserQueryClient) {
    browserQueryClient = createQueryClient();
    // Kick Discovery feed + card-image prefetch as early as the browser router boots.
    void startDiscoverySplashPrefetch(
      browserQueryClient,
      readStoredCity() ?? DEFAULT_LOCATION,
    );
  }

  return browserQueryClient;
}

export function getRouter(): AnyRouter {
  const queryClient = getQueryClient();

  if (typeof window !== "undefined") {
    if (!browserRouter) {
      browserRouter = createRouterInstance(queryClient);
      attachDepthPushVtBridge(browserRouter);
    }
    if (!browserTracingAttached) {
      Sentry.addIntegration(Sentry.tanstackRouterBrowserTracingIntegration(browserRouter));
      browserTracingAttached = true;
    }
    return browserRouter;
  }

  const router = createRouterInstance(queryClient);
  void startDiscoverySplashPrefetch(queryClient, readStoredCity() ?? DEFAULT_LOCATION);
  return router;
}
